/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faXmark,
  faPaperPlane,
} from '@fortawesome/pro-regular-svg-icons';
import { Antrag } from '@/domain/arbeitszeiten/entities';
import { ArbeitszeitArbeitszeitantragStatus } from '@/gql/graphql';
import { useTranslation } from 'react-i18next';

interface AntragVerlaufProps {
  antrag: Antrag;
}

function formatDateTime(date: Date): string {
  const weekday = date.toLocaleDateString('de-DE', { weekday: 'short' });
  const day = date.toLocaleDateString('de-DE', { day: '2-digit' });
  const month = date.toLocaleDateString('de-DE', { month: 'long' });
  const time = date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${weekday}, ${day}. ${month}, ${time} Uhr`;
}

function formatTimeRange(entry: Antrag): string {
  const beginn = entry.beginn2?.time
    ? entry.beginn2.time.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--:--';
  const ende = entry.ende2?.time
    ? entry.ende2.time.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--:--';
  return `${beginn} - ${ende}`;
}

function StatusIcon({
  status,
}: {
  status: ArbeitszeitArbeitszeitantragStatus | undefined;
}) {
  if (status === ArbeitszeitArbeitszeitantragStatus.Genehmigt) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-green-100 ring-1 ring-primary-green-300">
        <FontAwesomeIcon
          icon={faCheck}
          className="bg-primary-green-100 text-primary-green-600 text-xs"
        />
      </div>
    );
  }
  if (status === ArbeitszeitArbeitszeitantragStatus.Abgelehnt) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-red-100 ring-1 ring-primary-red-300">
        <FontAwesomeIcon
          icon={faXmark}
          className="bg-primary-red-100 text-primary-red-600 text-xs"
        />
      </div>
    );
  }
  return null;
}

function ErstellungIcon() {
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutrals-100 ring-1 ring-neutrals-400">
      <FontAwesomeIcon
        icon={faPaperPlane}
        className="text-neutrals-900 text-xs"
      />
    </div>
  );
}

function getStatusTitle(
  status: ArbeitszeitArbeitszeitantragStatus | undefined,
): string {
  if (status === ArbeitszeitArbeitszeitantragStatus.Abgelehnt)
    return 'domain.arbeitszeiten.antrag.abgelehnt';
  if (status === ArbeitszeitArbeitszeitantragStatus.Genehmigt)
    return 'domain.arbeitszeiten.antrag.genehmigt';
  return 'domain.arbeitszeiten.antrag.abgelehnt.antragAufArbeitszeit';
}

function TimelineRow({
  icon,
  title,
  timeRange,
  personName,
  timestamp,
  bemerkung,
  showLine,
}: {
  icon: React.ReactNode;
  title: string;
  timeRange: string;
  personName: string;
  timestamp: Date;
  bemerkung: string;
  showLine: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="relative flex gap-3">
      <div className="flex flex-col items-center shrink-0">
        {icon}
        {showLine && <div className="w-px flex-1 bg-neutrals-300 my-1" />}
      </div>

      <div className={`flex-1 min-w-0 ${showLine ? 'pb-4' : 'pb-0'}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-bold text-neutrals-900 leading-snug">
              {t(title)}
            </p>
            <p className="text-sm text-neutrals-700 mt-0.5">
              {'F\u00FCr '}
              {timeRange}
              {' Uhr von '}
              {personName}
            </p>
          </div>
          <span className="text-xs text-neutrals-600 whitespace-nowrap shrink-0 pt-0.5">
            {formatDateTime(timestamp)}
          </span>
        </div>

        {bemerkung && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-sm text-neutrals-800">{bemerkung}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function AntragVerlauf({ antrag }: AntragVerlaufProps) {
  const historie = antrag.historie || [];
  const { t } = useTranslation();

  if (historie.length === 0) return null;

  let totalRows = 0;
  for (const entry of historie) {
    const hasTwoDates =
      entry.erstellungsdatum &&
      entry.aenderungsdatum &&
      entry.erstellungsdatum.getTime() !== entry.aenderungsdatum.getTime();
    totalRows += hasTwoDates ? 2 : 1;
  }

  let rowIndex = 0;

  return (
    <div className="px-4 pb-8">
      <div className="border-t border-neutrals-300 pt-6">
        <h2 className="text-sm font-semibold text-neutrals-700 mb-6">
          {t('domain.arbeitszeiten.antrag.verlauf')}
        </h2>

        <div className="relative">
          {historie.map((entry) => {
            const personName = `${entry.mitarbeiter.vorname} ${entry.mitarbeiter.nachname}`;
            const timeRange = formatTimeRange(entry);
            const hasTwoDates =
              entry.erstellungsdatum &&
              entry.aenderungsdatum &&
              entry.erstellungsdatum.getTime() !==
                entry.aenderungsdatum.getTime();

            if (hasTwoDates) {
              const statusRowIndex = rowIndex++;
              const erstellungRowIndex = rowIndex++;

              return (
                <div key={entry.id}>
                  <TimelineRow
                    icon={<StatusIcon status={entry.status} />}
                    title={getStatusTitle(entry.status)}
                    timeRange={timeRange}
                    personName={personName}
                    timestamp={entry.aenderungsdatum!}
                    bemerkung={entry.bemerkungVorgesetzter || ''}
                    showLine={statusRowIndex < totalRows - 1}
                  />

                  <TimelineRow
                    icon={<ErstellungIcon />}
                    title={t(
                      'domain.arbeitszeiten.antrag.antragAufArbeitszeit',
                    )}
                    timeRange={timeRange}
                    personName={personName}
                    timestamp={entry.erstellungsdatum!}
                    bemerkung={entry.bemerkungAntragsteller || ''}
                    showLine={erstellungRowIndex < totalRows - 1}
                  />
                </div>
              );
            }

            const currentRowIndex = rowIndex++;
            const isErstellung =
              entry.status !== ArbeitszeitArbeitszeitantragStatus.Abgelehnt &&
              entry.status !== ArbeitszeitArbeitszeitantragStatus.Genehmigt;

            return (
              <TimelineRow
                key={entry.id}
                icon={
                  isErstellung ? (
                    <ErstellungIcon />
                  ) : (
                    <StatusIcon status={entry.status} />
                  )
                }
                title={
                  isErstellung
                    ? t('domain.arbeitszeiten.antrag.antragAufArbeitszeit')
                    : getStatusTitle(entry.status)
                }
                timeRange={timeRange}
                personName={personName}
                timestamp={
                  entry.aenderungsdatum ?? entry.erstellungsdatum ?? new Date()
                }
                bemerkung={
                  isErstellung
                    ? entry.bemerkungAntragsteller || ''
                    : entry.bemerkungVorgesetzter || ''
                }
                showLine={currentRowIndex < totalRows - 1}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
